import { useCallback, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { ConfigModal, DynamicFormTransformer } from '../../Config/Common/ConfigModal';
import { DataWithId, useConfigActions } from '../../Config/Common/UseConfigActions';
import { createFormFromFieldsArray, IForm, IFormField } from '../../../view-models/form.view-model';

interface Field3ConfigModalProps<T extends FieldValues & DataWithId> {
  title: string;
  formFields: IFormField<unknown>[];
  queryURL: string;
  /** Existing record to edit; omit for create. */
  item?: T;
  defaultValues?: Partial<T>;
  dynamicFormTransformer?: DynamicFormTransformer<T>;
  transformBeforeSubmit?: (data: T) => T;
  onClose: () => void;
}

/**
 * Standalone edit/create modal for the schema-driven (KPI / Column) field types.
 * Composes the same primitives `GenericConfigPage` uses internally — `ConfigModal`
 * for rendering and `useConfigActions` for the PUT/POST — so the Field Manager can
 * open a type's form without embedding its whole config page.
 */
export function Field3ConfigModal<T extends FieldValues & DataWithId>({
  title,
  formFields,
  queryURL,
  item,
  defaultValues,
  dynamicFormTransformer,
  transformBeforeSubmit,
  onClose,
}: Field3ConfigModalProps<T>) {
  const isEdit = item != null;
  const { updateItem } = useConfigActions<T>(queryURL);
  const [form] = useState<IForm<T>>(() =>
    createFormFromFieldsArray<T>(formFields, { data: (item ?? defaultValues ?? {}) as T })
  );

  const onSubmit = useCallback(
    async (data: T) => {
      const payload = transformBeforeSubmit ? transformBeforeSubmit(data) : data;
      await updateItem(payload, isEdit);
      onClose();
    },
    [transformBeforeSubmit, updateItem, isEdit, onClose]
  );

  return (
    <ConfigModal
      form={form}
      open
      onClose={onClose}
      onSubmit={onSubmit}
      title={title}
      dynamicFormTransformer={dynamicFormTransformer}
    />
  );
}
